<template>
  <b-card>
    <b-row>
      <b-col sm="4">
        <b-button
          class="w-100"
          variant="outline-primary"
          :to="{ name: 'organization-qr-generator-add' }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          {{ $t('Create QR') }}
        </b-button>
      </b-col>
      <b-col sm="4">
        <b-form-group>
          <b-form-input
            v-model="searchQuery"
            type="search"
            class="d-inline-block"
            :placeholder="$t('message.searchURL')"
            @input="searchQRCodes"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <filters
      :locationFilterShow="true"
      :dateRangeFilterShow="true"
      :resetDateButtonShow="true"
      dateRangePlaceholder="creation_date_filter"
      :physicalLocationFilterShow="true"
      :qrDestinationFiltersShow="true"
      :supervisorFilterShow="true"
      supervisorFilterPlaceholder="creator_filter"
      @changeFilter="updateFilter"
    />
    <b-row>
      <b-col cols="12">
        <b-table
          ref="refQRCodesListTable"
          :items="paginatedQRCodes"
          hover
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty=""
          :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
          :sort-desc.sync="isSortDirDesc"
        > 
          <template #cell(url)="data">
            <small>{{ data.item.url ? `${qrBaseURL(data.item.url).slice(0,25)}...` : '-' }}</small>
            <b-badge v-if="data.item.revoked" variant="danger" class="ml-50">
              {{ $t('label.revoked') }}
            </b-badge>
          </template>

          <template #cell(created_at)="data">
            <small>{{ data.item.created_at  && data.item.created_at}}</small>   
          </template>

          <template #cell(actions)="data">
            <div class="flex flex-column">
              <ViewButton
                v-if="data.item.imageKey"
                @clicked="openImageQRCodes(data.item.imageKey)"
              />
              <FeatherIconButton
                v-if="data.item.imageKey"
                icon="DownloadIcon"
                variant="primary"
                v-b-tooltip.hover.v-primary.bottom="$t('message.downloadQr')"
                @clicked="downloadImage(data.item.imageKey)"
              />
              <template v-if="!data.item.revoked">
                <FeatherIconButton
                  icon="RefreshCwIcon"
                  variant="warning"
                  v-b-tooltip.hover.v-warning.bottom="$t('message.regenerate')"
                  v-b-modal="`modal rotate-qr ${data.index}`"
                />
                <b-modal
                  :id="`modal rotate-qr ${data.index}`"
                  centered
                  no-close-on-backdrop
                  no-close-on-esc
                  modal-class="modal-warning"
                  ok-variant="warning"
                  cancel-variant="outline-secondary"
                  :title="$t('message.confirm_action')"
                  :ok-title="$t('message.regenerate')"
                  :cancel-title="$t('message.cancel')"
                  :hide-footer="actionInProgress === 'regenerate'"
                  @ok="confirmRegenerate($event, data.item, data.index)"
                >
                  <div v-if="actionInProgress === 'regenerate'" class="d-flex align-items-center">
                    <b-spinner small variant="warning" class="mr-1" />
                    <span>{{ $t('message.regeneratingQr') }}</span>
                  </div>
                  <span v-else>{{ $t('message.confirm_regenerate_qr') }}</span>
                </b-modal>

                <FeatherIconButton
                  icon="SlashIcon"
                  variant="danger"
                  v-b-tooltip.hover.v-danger.bottom="$t('message.revoke')"
                  v-b-modal="`modal revoke-qr ${data.index}`"
                />
                <b-modal
                  :id="`modal revoke-qr ${data.index}`"
                  centered
                  no-close-on-backdrop
                  no-close-on-esc
                  modal-class="modal-danger"
                  ok-variant="danger"
                  cancel-variant="outline-secondary"
                  :title="$t('message.confirm_action')"
                  :ok-title="$t('message.revoke')"
                  :cancel-title="$t('message.cancel')"
                  :hide-footer="actionInProgress === 'revoke'"
                  @ok="confirmRevoke($event, data.item, data.index)"
                >
                  <div v-if="actionInProgress === 'revoke'" class="d-flex align-items-center">
                    <b-spinner small variant="danger" class="mr-1" />
                    <span>{{ $t('message.revokingQr') }}</span>
                  </div>
                  <span v-else>{{ $t('message.confirm_revoke_qr') }}</span>
                </b-modal>
              </template>
            </div>
          </template>
        </b-table>
      </b-col>

      <b-col
        cols="12"
        class="d-flex justify-content-between flex-wrap"
      >
        <div class="mb-1">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="perPageOptions"
              class="w-50"
            />
          </b-form-group>
        </div>

        <div class="mb-1">
          <span class="text-muted">{{ showingMessage }}</span>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalQRCodes"
          :per-page="perPage"
          first-number=""
          last-number=""
          prev-class="prev-item"
          next-class="next-item"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import { BPagination, BTable, BModal, VBModal, BCol, BBadge, VBTooltip, BSpinner } from 'bootstrap-vue';
import { onMounted, ref, computed, getCurrentInstance } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import useqrCodesList from './useQRCodeList'
import vSelect from 'vue-select'
import Filters from "@/views/organization/Filters.vue"
import { qrBaseURL } from '../qrCodeImage'
import FeatherIconButton from '@/views/components/Shared/Buttons/FeatherIconButton.vue'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'

export default {
  components: {
    BTable,
    BPagination,
    BModal,
    BBadge,
    BSpinner,
    vSelect,
    Filters,
    FeatherIconButton,
    ViewButton
  },
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      items: [],
    }
  },
  setup() {
    const { router } = useRouter()
    const {
      fetchQRCodes,
      tableColumns,
      perPage,
      currentPage,
      perPageOptions,
      totalQRCodes,
      showingMessage,
      searchQuery,
      sortBy,
      isSortDirDesc,
      qrCodes,
      deleteQRcodes,
      revokeQRCode,
      regenerateQRCode,
      isLoading,
      searchQRCodes,
      destination,
      updateFilter,
      openImageQRCodes,
      downloadImage
    } = useqrCodesList()

    const paginatedQRCodes = computed(() => {
      const start = (currentPage.value - 1) * perPage.value
      const end = start + perPage.value
      return qrCodes.value.slice(start, end)
    })

    // Confirmation modals for Regenerate / Revoke. While the action runs the modal
    // stays open showing a loader, so an in-flight operation can not be confirmed twice.
    const instance = getCurrentInstance().proxy
    const actionInProgress = ref('')

    const confirmRegenerate = async (event, item, index) => {
      event.preventDefault()
      if (actionInProgress.value) return
      actionInProgress.value = 'regenerate'
      try {
        await regenerateQRCode(item)
      } finally {
        actionInProgress.value = ''
        instance.$bvModal.hide(`modal rotate-qr ${index}`)
      }
    }

    const confirmRevoke = async (event, item, index) => {
      event.preventDefault()
      if (actionInProgress.value) return
      actionInProgress.value = 'revoke'
      try {
        await revokeQRCode(item)
      } finally {
        actionInProgress.value = ''
        instance.$bvModal.hide(`modal revoke-qr ${index}`)
      }
    }

    onMounted(() => {
      fetchQRCodes()
    })

    return {
      fetchQRCodes,
      qrCodes,
      showingMessage,
      router,
      tableColumns,
      searchQuery,
      sortBy,
      isSortDirDesc,
      deleteQRcodes,
      revokeQRCode,
      regenerateQRCode,
      confirmRevoke,
      confirmRegenerate,
      actionInProgress,
      totalQRCodes,
      currentPage,
      perPageOptions,
      perPage,
      isLoading,
      searchQRCodes,
      paginatedQRCodes,
      destination,
      updateFilter,
      openImageQRCodes,
      downloadImage,
      qrBaseURL
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.process-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}

.form-group {
  label {
    font-size: 0.85rem;
    font-weight: 400;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
